import Button from "components/Button/Button";
import { Content } from "gls";
import React, { useEffect, useRef } from "react";
import { colours } from "styling/colours";
import { style } from "typestyle";
import AntModalStyled from "./AntModalStyled";
import { RoundedIcon } from "components/RoundedIcon";

type GenericModalProps = {
  isOpen: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actionName?: string;
  cancelName?: string;
  actionDisabled?: boolean;
  actionStyle?: "cancel" | "neutral";
  cancelStyle?: "cancel" | "neutral";
  icon?: React.ReactNode;
  roundIconStyle?: boolean;
  onClose?: () => void;
  onAction?: () => void;
  onCancel?: () => void;
  hideCloseButton?: boolean;
  maskColor?: string;
  loading?: boolean;
  children?: React.ReactNode;
  id?: string;
};

export const GenericModal = ({
  icon,
  roundIconStyle = true,
  title,
  subtitle,
  actionName,
  cancelName,
  actionStyle,
  actionDisabled,
  isOpen,
  onClose,
  onCancel,
  onAction,
  hideCloseButton = false,
  loading,
  children,
  id = 'modal',
}: GenericModalProps) => {

  const CancelButton = cancelName ?? "Cancel";
  const emptyFn = () => { }
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (actionButtonRef.current)
      actionButtonRef.current.focus();
    else
      cancelButtonRef.current?.focus();
  }, [isOpen])

  return (
    <AntModalStyled
      centered
      open={isOpen}
      onCancel={onCancel ?? onClose ?? emptyFn}
      footer={null}
      closable={!hideCloseButton}
      maskClosable={false}
    >
      <div id="modal-content">
        <header>
          {icon && roundIconStyle && <RoundedIcon fillColour={colours.secondaryText}>{icon}</RoundedIcon>}
          {icon && !roundIconStyle && icon}
          <h3 className={style({ color: colours.secondaryText })}>{title}</h3>
        </header>
        <Content padding={[20, 0]} id="modal-content">
          {subtitle && <span className={style({ color: colours.primaryText })}>{subtitle}</span>}
          {children}
        </Content>
        {(CancelButton || actionName) && (
          <footer id="modal-footer">
            {CancelButton &&
              <Button
                ref={cancelButtonRef}
                id="modal-cancel-btn"
                disabled={loading}
                outline
                onClick={onCancel ?? onClose ?? emptyFn}>
                {CancelButton}
              </Button>
            }
            {actionName && (
              <Button
                ref={actionButtonRef}
                styling={actionStyle}
                onClick={onAction ?? emptyFn}
                disabled={actionDisabled}
                isLoading={loading}
                id={`${id}-action-btn`}
              >
                {actionName}
              </Button>
            )}
          </footer>
        )}
      </div>
    </AntModalStyled>
  );
};
